import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useWishlist } from "../../context/WishlistContext";
import { useNotification } from "../../context/NotificationContext";
import ProfileDropdownMenu from "./ProfileDropdownMenu";
import SellerProfileDropdownMenu from "../seller/SellerProfileDropdownMenu";
import { useAuth } from "../../auth/AuthContext";
import { productService } from "../../services/productService";
import useGeolocation from "./location/useGeolocation";
import LocationPermissionModal from "./location/LocationPermissionModal";
import Dropdown from "./ReusableDropdown";
import { parseSearchIntent } from "../../utils/searchIntent";

const RECENT_SEARCHES_KEY = "akkorent_recent_searches";
const DISCOVERY_SEARCHES = ["Bike", "Camera", "Furniture", "House"];

const formatSearchLabel = (value) => String(value || "").replace(/_/g, " ");

const HighlightedText = ({ text, query }) => {
  const value = String(text || "");
  const terms = String(query || "").trim().split(/\s+/).filter(Boolean);
  if (!terms.length) return value;
  const escaped = terms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const matcher = new RegExp(`(${escaped.join("|")})`, "ig");
  return value.split(matcher).map((part, index) =>
    terms.some((term) => part.toLowerCase() === term.toLowerCase())
      ? <strong key={`${part}-${index}`} className="font-bold text-gray-950 dark:text-white">{part}</strong>
      : <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
  );
};

const productImage = (product) => product?.thumbnail || product?.media?.[0]?.url || product?.media?.images?.[0]?.url;

/* ─── Icons ─────────────────────────────────────────────── */
 const ChevronDown = ({ size = 14, className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const SearchIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

const HeartIcon = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const BagIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <path d="M16 10a4 4 0 0 1-8 0" />
  </svg>
);

const UserIcon = ({ size = 18 }) => (
  <svg
    width="13"
    height="19"
    viewBox="0 0 18 23"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8.75 10C9.73891 10 10.7056 9.70676 11.5279 9.15735C12.3501 8.60794 12.991 7.82705 13.3694 6.91342C13.7478 5.99979 13.8469 4.99446 13.6539 4.02455C13.461 3.05465 12.9848 2.16373 12.2855 1.46447C11.5863 0.765205 10.6954 0.289002 9.72545 0.0960758C8.75555 -0.0968502 7.75021 0.00216641 6.83658 0.380605C5.92295 0.759043 5.14206 1.3999 4.59265 2.22215C4.04325 3.0444 3.75 4.0111 3.75 5C3.75 6.32608 4.27678 7.59785 5.21447 8.53554C6.15215 9.47322 7.42392 10 8.75 10ZM8.75 2.5C9.24445 2.5 9.7278 2.64662 10.1389 2.92133C10.55 3.19603 10.8705 3.58648 11.0597 4.04329C11.2489 4.50011 11.2984 5.00278 11.202 5.48773C11.1055 5.97268 10.8674 6.41814 10.5178 6.76777C10.1681 7.1174 9.72268 7.3555 9.23773 7.45197C8.75277 7.54843 8.25011 7.49892 7.79329 7.3097C7.33648 7.12048 6.94603 6.80005 6.67133 6.38893C6.39662 5.9778 6.25 5.49446 6.25 5C6.25 4.33696 6.51339 3.70108 6.98223 3.23223C7.45107 2.76339 8.08696 2.5 8.75 2.5ZM8.75 12.5C6.42936 12.5 4.20376 13.4219 2.56282 15.0628C0.921872 16.7038 0 18.9294 0 21.25C0 21.5815 0.131696 21.8995 0.366117 22.1339C0.600537 22.3683 0.918479 22.5 1.25 22.5C1.58152 22.5 1.89946 22.3683 2.13388 22.1339C2.3683 21.8995 2.5 21.5815 2.5 21.25C2.5 19.5924 3.15848 18.0027 4.33058 16.8306C5.50269 15.6585 7.0924 15 8.75 15C10.4076 15 11.9973 15.6585 13.1694 16.8306C14.3415 18.0027 15 19.5924 15 21.25C15 21.5815 15.1317 21.8995 15.3661 22.1339C15.6005 22.3683 15.9185 22.5 16.25 22.5C16.5815 22.5 16.8995 22.3683 17.1339 22.1339C17.3683 21.8995 17.5 21.5815 17.5 21.25C17.5 18.9294 16.5781 16.7038 14.9372 15.0628C13.2962 13.4219 11.0706 12.5 8.75 12.5Z"
      fill="#151D2D"
    />
  </svg>
);

const MoonIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const SunIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    color="gold"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

const LocationPinIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const BellIcon = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </svg>
);

/* ─── Spinner ────────────────────────────────────────────── */
const Spinner = () => (
  <svg
    className="animate-spin h-4 w-4 text-blue-500"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
    />
  </svg>
);

/* ─── Main Navbar ────────────────────────────────────────── */
const Navbar = ({ toggleDarkMode, darkMode }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [sellerDropdownOpen, setSellerDropdownOpen] = useState(false);
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  const {
    selectedCity,
    loading: locationLoading,
    showPermissionPrompt,
    detectLocation,
    handleDenyPermission,
    setManualCity,
  } = useGeolocation();
  //const [selectedCity, setSelectedCity] = useState(detectedCity || "Select City");

  const location = useLocation();
  const navigate = useNavigate();
  const isTypesenseSearchPage = ["/products", "/typesense-products"].includes(location.pathname);

  const [suggestions, setSuggestions] = useState([]);
  const [discoveryProducts, setDiscoveryProducts] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || "[]");
    } catch {
      return [];
    }
  });
  const searchContainerRef = useRef(null);
  const mobileSearchContainerRef = useRef(null);
  const mobileSearchToggleRef = useRef(null);
  const mobileSearchInputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const sellerDropdownRef = useRef(null);
  const cityDropdownRef = useRef(null);
  const mobileCityDropdownRef = useRef(null);

  useEffect(() => {
    if (!isTypesenseSearchPage) return undefined;
    const syncTypesenseQuery = (event) => setSearchQuery(event.detail || "");
    window.addEventListener("typesenseQueryChanged", syncTypesenseQuery);
    return () => window.removeEventListener("typesenseQueryChanged", syncTypesenseQuery);
  }, [isTypesenseSearchPage]);

  const { user, isAuthenticated, authReady } = useAuth();
  const { unreadCount } = useNotification();
  const hasResolvedUser = isAuthenticated && !!user;
  const isSeller = user && user.roles && user.roles.includes("seller");
  const isResolvingSessionUser = !authReady || (isAuthenticated && !user);

  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isMobileProfileDropdownOpen, setIsMobileProfileDropdownOpen] = useState(false);
  const profileDropdownRef = useRef(null);
  const mobileProfileDropdownRef = useRef(null);
  const { logout } = useAuth();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const { wishlistCount } = useWishlist();
  const [showMobileBottomNav, setShowMobileBottomNav] = useState(true);
  const lastScrollY = useRef(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleScroll = () => {
      const currentY = window.scrollY;
      const isScrollingDown = currentY > lastScrollY.current;
      const isScrollingUp = currentY < lastScrollY.current;

      lastScrollY.current = currentY;
      if (currentY <= 20) {
        setShowMobileBottomNav(true);
      } else if (isScrollingDown) {
        setShowMobileBottomNav(false);
      } else if (isScrollingUp) {
        setShowMobileBottomNav(true);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.style.setProperty(
      "--app-mobile-bottom-offset",
      showMobileBottomNav ? "7.5rem" : "1.5rem"
    );
  }, [showMobileBottomNav]);

  // Seller paths 
  const sellerPath = [
                  {
                    to: "/seller/listings/add",
                    label: "List Product",
                    requiresSeller: false,
                  },
                  {
                    to: "/seller",
                    label: "Seller Dashboard",
                    requiresSeller: true,
                  },
                  {
                    to: "/seller/listings",
                    label: "My Listings",
                    requiresSeller: true,
                  },
                  {
                    to: "/account",
                    label: "Account",
                    requiresSeller: true,
                  },
                ]

  const handleLogoutClick = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      console.error("Logout error:", error);
      setIsLoggingOut(false);
    }
  };

  const handleSellerNavigation = useCallback(
    (targetPath, requiresSellerRole = true) => {
      console.log("Navigation triggered:", {
        targetPath,
        requiresSellerRole,
        hasResolvedUser,
        isSeller,
      });
      setSellerDropdownOpen(false);

      if (!hasResolvedUser) {
        console.log("User not logged in, redirecting to login");
        navigate(`/login?from=${encodeURIComponent(targetPath)}`);
        return;
      }

      if (requiresSellerRole && !isSeller) {
        console.log("User not seller, redirecting to add listing");
        navigate("/seller/listings/add");
        return;
      }

      console.log("Normal navigation to:", targetPath);
      navigate(targetPath);
    },
    [hasResolvedUser, isSeller, navigate],
  );

  const handleBackdropClick = (e) => {
    if (!isLoggingOut && e.target === e.currentTarget) {
      setShowLogoutModal(false);
    }
  };

  const cities = [
    "Mumbai",
    "Delhi",
    "Bangalore",
    "Hyderabad",
    "Chennai",
    "Pune",
    "Kolkata",
    "Ahmedabad",
  ];

  /* ── Close dropdowns on outside click ───────────────────── */
  useEffect(() => {
    const handler = (e) => {
      // Check if click is inside desktop OR mobile city dropdown
      const isDesktopDropdown = cityDropdownRef.current?.contains(e.target);
      const isMobileDropdown = mobileCityDropdownRef.current?.contains(
        e.target,
      );

      // If click is outside BOTH dropdowns, close them
      if (!isDesktopDropdown && !isMobileDropdown) {
        setCityDropdownOpen(false);
      }

      // Seller dropdown
      if (
        sellerDropdownRef.current &&
        !sellerDropdownRef.current.contains(e.target)
      ) {
        setSellerDropdownOpen(false);
      }

      if (!profileDropdownRef.current?.contains(e.target)) {
        setIsProfileDropdownOpen(false);
      }
      if (!mobileProfileDropdownRef.current?.contains(e.target)) {
        setIsMobileProfileDropdownOpen(false);
      }

      // Only one search container is mounted at each responsive breakpoint.
      const isDesktopSearch = searchContainerRef.current?.contains(e.target) || false;
      const isMobileSearch = mobileSearchContainerRef.current?.contains(e.target) || false;
      const isMobileSearchToggle = mobileSearchToggleRef.current?.contains(e.target) || false;
      if (!isDesktopSearch && !isMobileSearch && !isMobileSearchToggle) {
        setShowSuggestions(false);
        setIsMobileSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ── Live search ─────────────────────────────────────────── */
  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(Boolean(isTypesenseSearchPage && !query?.trim()));
      return;
    }
    setLoadingSuggestions(true);
    try {
      const { typesenseCollection, typesenseSearchClient } = await import("../../config/typesense");
      if (typesenseSearchClient) {
        const response = await typesenseSearchClient.search([{
          indexName: typesenseCollection,
          params: { query: query.trim(), hitsPerPage: 5 },
        }]);
        const hits = response.results?.[0]?.hits || [];
        setSuggestions(hits);
        setShowSuggestions(true);
        return;
      }
      const data = await productService.searchProducts({
        q: query.trim(),
        limit: 5,
      });
      if (data.ok && Array.isArray(data.items)) {
        setSuggestions(data.items);
        setShowSuggestions(data.items.length > 0);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch {
      setSuggestions([]);
      setShowSuggestions(isTypesenseSearchPage);
    } finally {
      setLoadingSuggestions(false);
    }
  }, [isTypesenseSearchPage]);

  const fetchDiscoveryProducts = useCallback(async () => {
    if (discoveryProducts.length) return;
    try {
      const { typesenseCollection, typesenseSearchClient } = await import("../../config/typesense");
      if (!typesenseSearchClient) return;
      const response = await typesenseSearchClient.search([{
        indexName: typesenseCollection,
        params: { query: "*", hitsPerPage: 4 },
      }]);
      setDiscoveryProducts(response.results?.[0]?.hits || []);
    } catch {
      setDiscoveryProducts([]);
    }
  }, [discoveryProducts.length]);

  const openSearchDiscovery = () => {
    if (isTypesenseSearchPage || suggestions.length > 0) setShowSuggestions(true);
    if (!searchQuery.trim()) fetchDiscoveryProducts();
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setActiveSuggestionIndex(-1);
    if (isTypesenseSearchPage) {
      window.dispatchEvent(new CustomEvent("typesenseNavbarQuery", { detail: value }));
      setShowSuggestions(true);
    }
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => fetchSuggestions(value), 400);
  };

  const rememberSearch = (value) => {
    const normalized = value.trim();
    if (!normalized) return;
    const next = [normalized, ...recentSearches.filter((item) => item.toLowerCase() !== normalized.toLowerCase())].slice(0, 5);
    setRecentSearches(next);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next));
  };

  const applyTypesenseSearch = (value) => {
    setSearchQuery(value);
    setShowSuggestions(true);
    window.dispatchEvent(new CustomEvent("typesenseNavbarQuery", { detail: value }));
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => fetchSuggestions(value), 150);
  };

  const clearNavbarSearch = () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    setSearchQuery("");
    setSuggestions([]);
    setActiveSuggestionIndex(-1);
    setShowSuggestions(isTypesenseSearchPage);
    if (isTypesenseSearchPage) {
      window.dispatchEvent(new CustomEvent("typesenseNavbarQuery", { detail: "" }));
    }
  };

  const buildSearchUrl = (query) => {
    const base = location.pathname === "/products" ? location.search : "";
    const params = new URLSearchParams(base);
    const intent = parseSearchIntent(query);
    if (intent.query) params.set("q", intent.query);
    else params.delete("q");
    if (intent.price) {
      params.set("price", `${intent.price[0] ?? ""}:${intent.price[1] ?? ""}`);
      params.delete("page");
    }
    return `/products?${params.toString()}`;
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (activeSuggestionIndex >= 0 && suggestions[activeSuggestionIndex]) {
      handleSuggestionClick(suggestions[activeSuggestionIndex]);
      return;
    }
    setShowSuggestions(false);
    if (searchQuery.trim()) {
      const intent = parseSearchIntent(searchQuery);
      if (intent.price) {
        rememberSearch(searchQuery);
        navigate(buildSearchUrl(searchQuery));
        setSearchQuery(intent.query);
        setIsMobileSearchOpen(false);
        return;
      }
      if (isTypesenseSearchPage) {
        rememberSearch(searchQuery);
        window.dispatchEvent(new CustomEvent("typesenseNavbarQuery", { detail: searchQuery.trim() }));
        return;
      }
      navigate(buildSearchUrl(searchQuery));
      setSearchQuery("");
      setIsMenuOpen(false);
    }
  };

  const handleSearchKeyDown = (event) => {
    if (!showSuggestions || !suggestions.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveSuggestionIndex((current) => Math.min(current + 1, suggestions.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveSuggestionIndex((current) => Math.max(current - 1, -1));
    } else if (event.key === "Escape") {
      setShowSuggestions(false);
      setActiveSuggestionIndex(-1);
    }
  };

  const handleSuggestionClick = (product) => {
    rememberSearch(searchQuery);
    setShowSuggestions(false);
    setSearchQuery("");
    setIsMenuOpen(false);
    navigate(`/products/product-details/${product.id}`);
  };

  const handleCategoryClick = (category) => {
    const params = new URLSearchParams(location.pathname === "/products" ? location.search : "");
    params.delete("q");
    params.delete("page");
    params.set("category", category);
    setSearchQuery("");
    setShowSuggestions(false);
    setIsMobileSearchOpen(false);
    navigate(`/products?${params.toString()}`);
  };

  const handleViewAllResults = () => {
    setShowSuggestions(false);
    if (searchQuery.trim()) {
      const intent = parseSearchIntent(searchQuery);
      if (intent.price) {
        rememberSearch(searchQuery);
        navigate(buildSearchUrl(searchQuery));
        setSearchQuery(intent.query);
        setIsMobileSearchOpen(false);
        return;
      }
      if (isTypesenseSearchPage) {
        rememberSearch(searchQuery);
        window.dispatchEvent(new CustomEvent("typesenseNavbarQuery", { detail: searchQuery.trim() }));
        return;
      }
      navigate(buildSearchUrl(searchQuery));
      setSearchQuery("");
      setIsMenuOpen(false);
    }
  };

  const handleLogoClick = (e) => {
  if (location.pathname === "/") {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
};

  const greeting = (() => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    return "Good Evening";
  })();

  const userName = hasResolvedUser
    ? user.first_name || user.firstName
      ? `${user.first_name || user.firstName}`
      : user.name || user.username || "Hey!"
    : "Hey!";

  /* ─── Suggestion Dropdown ──────────────────────────────── */
  const SuggestionDropdown = () => {
    const categorySuggestions = [...new Set(suggestions.map((product) => product.category).filter(Boolean))].slice(0, 3);
    const querySuggestions = [...new Set(suggestions.map((product) => product.title).filter(Boolean))].slice(0, 4);
    const productsToPreview = searchQuery.trim() ? suggestions : discoveryProducts;

    const ProductPreview = ({ product, index }) => (
      <button
        type="button"
        onMouseEnter={() => setActiveSuggestionIndex(index)}
        onClick={() => handleSuggestionClick(product)}
        className={`group flex min-w-0 h-full flex-col overflow-hidden rounded-xl border text-left shadow-sm transition-all duration-200 ${activeSuggestionIndex === index ? "border-blue-300 bg-blue-50 shadow-md dark:border-blue-700 dark:bg-blue-950/40" : "border-gray-200 bg-white hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-md dark:border-gray-700 dark:bg-gray-900 dark:hover:border-blue-800"}`}
      >
        <div className="aspect-[4/3] w-full shrink-0 overflow-hidden bg-gray-100 dark:bg-gray-800">
          {productImage(product) ? (
            <img src={productImage(product)} alt="" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
          ) : (
            <div className="flex h-full items-center justify-center text-gray-400"><SearchIcon size={18} /></div>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col p-2.5">
          <p className="truncate text-xs font-bold text-gray-900 dark:text-white"><HighlightedText text={product.title} query={searchQuery} /></p>
          <p className="mt-1 truncate text-[11px] capitalize text-gray-400">{formatSearchLabel(product.category)}{product.city ? ` · ${product.city}` : ""}</p>
          {product.price != null && <p className="mt-1 truncate text-xs font-semibold text-blue-600">₹{product.price}/{product.price_unit || "day"}</p>}
        </div>
      </button>
    );

   return (
      <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-[min(70vh,560px)] max-w-[calc(100vw-2rem)] overflow-y-auto rounded-sm border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900 md:left-1/2 md:right-auto md:-translate-x-[62%] md:w-[calc(100vw-3rem)] md:max-w-[calc(100vw-3rem)] lg:w-[min(980px,calc(100vw-2rem))] lg:max-w-[calc(100vw-2rem)]">
        {loadingSuggestions ? (
          <div className="flex items-center gap-2 px-5 py-6 text-base text-gray-500"><Spinner /> Searching rentals…</div>
        ) : !searchQuery.trim() ? (
          <div className="grid gap-6 p-4 md:grid-cols-[280px_1fr] md:p-5">
            <div>
              {recentSearches.length > 0 && (
                <div className="mb-6">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-xs font-medium text-gray-400">Your searches</p>
                    <button type="button" onClick={() => { localStorage.removeItem(RECENT_SEARCHES_KEY); setRecentSearches([]); }} className="text-xs font-semibold text-blue-600">Clear</button>
                  </div>
                  <div>
                    {recentSearches.map((item) => (
                      <button key={item} type="button" onClick={() => applyTypesenseSearch(item)} className="flex w-full items-center gap-3 py-2 text-left text-base text-gray-700 hover:text-blue-600 dark:text-gray-200">
                        <span className="text-gray-400">↻</span><span className="font-semibold">{item}</span><span className="ml-auto text-gray-400">↗</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <p className="mb-2 text-xs font-medium text-gray-400">Popular searches</p>
              <div className="flex flex-wrap gap-2">
                {DISCOVERY_SEARCHES.map((item) => (
                  <button key={item} type="button" onClick={() => applyTypesenseSearch(item)} className="rounded-sm border border-gray-300 bg-gray-100 px-3 py-2 text-xs text-gray-800 hover:border-blue-300 hover:text-blue-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">{item}</button>
                ))}
              </div>
            </div>
            <div className="min-w-0">
              <p className="mb-3 text-xs font-medium text-gray-400">Trending rentals</p>
              {productsToPreview.length ? (
                <div className="grid grid-cols-2 gap-3 min-[380px]:grid-cols-2 md:grid-cols-4">{productsToPreview.slice(0, 4).map((product, index) => <ProductPreview key={product.id} product={product} index={index} />)}</div>
              ) : (
                <p className="text-base text-gray-400">Start typing to discover rentals nearby.</p>
              )}
            </div>
          </div>
        ) : suggestions.length ? (
          <>
            <div className="grid md:grid-cols-[330px_1fr]">
              <div className="border-b border-gray-100 p-4 dark:border-gray-800 md:border-b-0 md:border-r">
                <p className="mb-2 text-xs font-medium text-gray-400">Search suggestions</p>
                <button type="button" onClick={handleViewAllResults} className="flex w-full items-center gap-3 py-2 text-left text-base text-gray-700 hover:text-blue-600 dark:text-gray-200">
                  <SearchIcon size={16} /><span>Search for <strong className="font-bold">“{searchQuery.trim()}”</strong></span><span className="ml-auto">↗</span>
                </button>
                {querySuggestions.map((title) => (
                  <button key={title} type="button" onClick={() => applyTypesenseSearch(title)} className="flex w-full items-center gap-3 py-2 text-left text-base text-gray-700 hover:text-blue-600 dark:text-gray-200">
                    <SearchIcon size={16} /><span className="truncate"><HighlightedText text={title} query={searchQuery} /></span><span className="ml-auto">↗</span>
                  </button>
                ))}
                {categorySuggestions.map((category) => (
                  <button key={category} type="button" onClick={() => handleCategoryClick(category)} className="flex w-full items-center gap-3 py-2 text-left text-base text-gray-700 hover:text-blue-600 dark:text-gray-200">
                    <span className="grid h-4 w-4 grid-cols-2 gap-px"><i className="bg-current" /><i className="bg-current" /><i className="bg-current" /><i className="bg-current" /></span>
                    <span className="truncate capitalize"><HighlightedText text={formatSearchLabel(category)} query={searchQuery} /></span><span className="ml-auto text-xs text-gray-400">Category</span>
                  </button>
                ))}
              </div>
              <div className="min-w-0 p-4">
                <p className="mb-3 text-xs font-medium text-gray-400">Products</p>
                <div className="grid grid-cols-2 gap-3 min-[380px]:grid-cols-2 md:grid-cols-4">{suggestions.slice(0, 4).map((product, index) => <ProductPreview key={product.id} product={product} index={index} />)}</div>
              </div>
            </div>
            <button type="button" onClick={handleViewAllResults} className="w-full border-t border-gray-100 px-4 py-3 text-center text-base font-bold text-blue-600 hover:bg-blue-50 dark:border-gray-800 dark:hover:bg-gray-800">View all results for “{searchQuery.trim()}”</button>
          </>
        ) : (
          <div className="grid gap-6 p-5 md:grid-cols-[320px_1fr]">
            <div>
              <h3 className="text-base font-bold text-gray-900 dark:text-white">No matches for “{searchQuery.trim()}”</h3>
              <p className="mt-4 text-base font-semibold text-gray-700 dark:text-gray-200">Try the following:</p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-base text-gray-500"><li>Check the spelling</li><li>Use fewer or more general words</li><li>Browse a rental category</li></ul>
              <p className="mb-2 mt-5 text-xs font-medium text-gray-400">Popular searches</p>
              <div className="flex flex-wrap gap-2">{DISCOVERY_SEARCHES.map((item) => <button key={item} type="button" onClick={() => applyTypesenseSearch(item)} className="rounded-sm border border-gray-300 bg-gray-100 px-3 py-2 text-xs dark:border-gray-700 dark:bg-gray-800">{item}</button>)}</div>
            </div>
            <div>
              <p className="mb-3 text-xs font-medium text-gray-400">Explore rentals</p>
              <div className="grid grid-cols-2 gap-3 min-[380px]:grid-cols-2 md:grid-cols-4">{discoveryProducts.slice(0, 4).map((product, index) => <ProductPreview key={product.id} product={product} index={index} />)}</div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Location Permission Modal */}
      <LocationPermissionModal
        isOpen={showPermissionPrompt}
        onAllow={detectLocation}
        onDeny={handleDenyPermission}
        isLoading={locationLoading}
      />

      {/* ── Navbar ── */}
      <nav
        className="sticky top-0 z-50 border-b border-[#E4E4E7] bg-white dark:border-gray-800 dark:bg-gray-950"
      >
        <div className="hidden md:flex max-w-[1440px] mx-auto px-6 h-20 items-center gap-3">
          {/* Logo */}
          <Link
            to="/"
            onClick={handleLogoClick}
            className="flex items-center gap-2 flex-shrink-0 no-underline"
          >
            <img
              src="/logo.png"
              alt="AkkoRent"
              className="w-9 h-9 rounded-xl object-cover"
            />
            <span className="sm:inline md:hidden lg:inline text-blue-600 dark:text-blue-500 font-bold text-xl tracking-tight">
              AkkoRent
            </span>
          </Link>

          {/* ── For Sellers dropdown (Simple button, dropdown items have Figma style) ── */}
          <Dropdown  containerClassName="hidden md:block" buttonClassName="
flex items-center gap-1.5
text-base font-medium
text-gray-700 dark:text-gray-200
hover:text-blue-600
transition-colors
px-2 py-1.5
rounded-lg
hover:bg-gray-50 dark:hover:bg-gray-800
" ref={sellerDropdownRef} dropdownClassName="w-40 overflow-hidden p-1.5" isOpen={sellerDropdownOpen} setIsOpen={()=> {setSellerDropdownOpen((prev) => !prev)}} trigger={"For Seller"} >
            {sellerPath.map((item) => {
              return  <button
                    key={item.to}
                    onClick={() =>
                      handleSellerNavigation(item.to, item.requiresSeller)
                    }
                    className="
            block w-full text-left px-2 py-2 mt-1 text-base
            rounded-full
            transition-all duration-200
            text-gray-700 dark:text-gray-300
            hover:bg-[#3B82F6]
            hover:text-white
            hover:shadow-[0px_4px_4px_0px_#00000040]
            active:bg-[#014EF8]
            active:shadow-[0px_4px_4px_0px_#00000040]
          "
                  >
                    {item.label}
                  </button>
            })}
          </Dropdown>

          {/* Products Button (Desktop) */}
          <Link
            to={"/products"}
            className={`hidden md:flex items-center gap-1.5 text-base font-medium px-2 py-2 rounded-lg transition-colors no-underline
              ${
                location.pathname === "/products"
                  ? "text-blue-600 bg-blue-50 dark:bg-blue-950"
                  : "text-gray-700 dark:text-gray-200 hover:text-blue-600 hover:bg-gray-50 dark:hover:bg-gray-800"
              }
            `}
          >
            <BagIcon />
            <span className="hidden text-base lg:inline">Products</span>
          </Link>

          {/* ── City Selector (Simple button, dropdown items have Figma style) ── */}
         <Dropdown
  containerClassName="hidden md:block"
  buttonClassName="
    flex items-center gap-1.5
    text-base font-medium
    text-gray-700 dark:text-gray-200
    hover:text-blue-600
    transition-colors
    px-2 py-1.5
    rounded-lg
    hover:bg-gray-50 dark:hover:bg-gray-800
  "
  ref={cityDropdownRef}
  dropdownClassName="w-44 max-h-[253px] overflow-y-auto show-scrollbar py-2 px-1"
  isOpen={cityDropdownOpen}
  setIsOpen={() => setCityDropdownOpen((prev) => !prev)}
  trigger={
    <>
      <LocationPinIcon />
      <span className="text-base">
        {locationLoading ? "Detecting..." : selectedCity || "Select City"}
      </span>
    </>
  }
>
  <button
    onClick={() => {
      detectLocation();
      setCityDropdownOpen(false);
    }}
    className="
      flex items-center gap-2 w-full text-left px-2 py-2 text-base mb-1
      rounded-full
      transition-all duration-200
      text-gray-700 dark:text-gray-300
      hover:bg-[#3B82F6] hover:text-white hover:shadow-md hover:shadow-blue-500/25
      active:bg-[#014EF8] active:shadow-sm active:shadow-blue-500/30 active:scale-[0.97]
    "
  >
    <LocationPinIcon />
    <span className="whitespace-nowrap">Detect My Location</span>
  </button>

  <div className="border-t border-gray-100 dark:border-gray-700 my-1" />

  {cities.map((city) => (
    <button
      key={city}
      onClick={() => {
        setManualCity(city);
        setCityDropdownOpen(false);
      }}
      className={`
        flex items-center gap-2 w-full text-left px-2 py-2 mt-1 text-base
        rounded-full
        transition-all duration-200
        ${
          selectedCity === city
            ? "bg-[#2563EB] text-white shadow-sm shadow-blue-500/30"
            : "text-gray-700 dark:text-gray-300 hover:bg-[#3B82F6] hover:text-white hover:shadow-md hover:shadow-blue-500/25 active:bg-[#014EF8] active:shadow-sm active:shadow-blue-500/30 active:scale-[0.97]"
        }
      `}
    >
      <LocationPinIcon />
      {city}
    </button>
  ))}
</Dropdown>

          {/* ── Search Bar ── */}
          <div className="flex min-w-0 flex-1 justify-center px-2">
            <form
              className="hidden w-full md:block"
              onSubmit={handleSearchSubmit}
              ref={searchContainerRef}
            >
              <div className="relative">
                <div className="flex h-12 items-center gap-1 rounded-xl border border-[#D3D4D8] bg-[#F4F4F5] px-4 shadow-sm transition-all hover:border-blue-300 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/15 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-blue-500">
                  <button type="submit" className="flex-shrink-0 text-gray-400 transition-colors hover:text-blue-600" aria-label="Search products">
                    <SearchIcon size={16} />
                  </button>
                  <input
                    type="text"
                    placeholder="Search product..."
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    onKeyDown={handleSearchKeyDown}
                    onFocus={openSearchDiscovery}
                    className="min-w-0 flex-1 bg-transparent text-base text-gray-700 dark:text-gray-200 placeholder-gray-400 outline-none"
                  />
                  {searchQuery && (
                    <button type="button" onClick={clearNavbarSearch} className="flex-shrink-0 rounded-full px-1 text-lg leading-none text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" aria-label="Clear product search">×</button>
                  )}
                </div>
                {showSuggestions && <SuggestionDropdown />}
              </div>
            </form>
          </div>

          {/* ── Right Actions ── */}
          <div className="hidden md:flex items-center gap-1 flex-shrink-0 ml-auto">
            {/* Dark Mode */}
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-full text-gray-500 dark:text-gray-300 hover:text-blue-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
            >
              {darkMode ? <SunIcon /> : <MoonIcon />}
            </button>

            {/* Wishlist */}
            <Link
              to="/wishlist"
              className={`relative p-2 rounded-full transition-all no-underline ${
                location.pathname === "/wishlist"
                  ? "text-blue-600 bg-blue-50 dark:bg-blue-950"
                  : "text-gray-500 dark:text-gray-300 hover:text-blue-600 hover:bg-gray-100 dark:hover:bg-gray-800"
              }`}
            >
              <HeartIcon />
              {wishlistCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold">
                  {wishlistCount}
                </span>
              )}
            </Link>

            {/* User / Login */}
            {hasResolvedUser ? (
              <div className="relative" ref={profileDropdownRef}>
                <button
                  type="button"
                  onClick={() => {
                    setIsMobileProfileDropdownOpen(false);
                    setIsProfileDropdownOpen((open) => !open);
                  }}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl text-base font-medium text-gray-700 dark:text-gray-200 hover:text-blue-600 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
                >
                  <div className="w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {user.avatar ? (
                      <img
                        src={user.avatar}
                        alt={userName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <svg
                        className="w-4 h-4 text-white"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <circle cx="12" cy="8" r="4" />
                        <path d="M4 20c0-4 4-7 8-7s8 3 8 7" />
                      </svg>
                    )}
                  </div>
                  <span className="hidden lg:inline">{userName}</span>
                  <ChevronDown
                    className={`hidden lg:block mt-1 transition-transform ${isProfileDropdownOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {isProfileDropdownOpen &&
                  (isSeller ? (
                    <SellerProfileDropdownMenu
                      user={user}
                      toggleDarkMode={toggleDarkMode}
                      darkMode={darkMode}
                      onClose={() => setIsProfileDropdownOpen(false)}
                    />
                  ) : (
                    <ProfileDropdownMenu
                      user={user}
                      toggleDarkMode={toggleDarkMode}
                      darkMode={darkMode}
                      onClose={() => setIsProfileDropdownOpen(false)}
                    />
                  ))}
              </div>
            ) : isResolvingSessionUser ? (
              <div className="flex items-center gap-2 px-3 py-2 text-base text-gray-400 dark:text-gray-500">
                <div className="w-7 h-7 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
              </div>
            ) : (
              <Link
                to="/login"
                className="flex items-center gap-2 px-3 py-2 rounded-xl text-base font-medium text-gray-700 dark:text-gray-200 hover:text-blue-600 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all no-underline"
              >
                <div className="bg-[#EFF6FF] dark:bg-gray-400 dark:text-white h-[30px] w-[30px] rounded-full flex items-center justify-center flex-shrink-0">
                  <UserIcon />
                </div>
                <span className="hide text-base md:hidden lg:block">
                  Hey! Log in
                </span>
              </Link>
            )}
          </div>

        </div>

        {/* ── Mobile Header (Home-style) ── */}
        <div className="md:hidden px-4 pt-4 pb-2">
          <div className="flex items-center justify-between gap-2 mb-3">
            {/* Logo + Greeting + City */}
            <Link
              to="/"
              onClick={handleLogoClick}
              className="flex items-center gap-2 min-w-0 no-underline flex-1"
            >
              <img
                src="/logo.png"
                alt="AkkoRent"
                className="w-12 h-12 rounded-xl object-cover flex-shrink-0"
              />
              <div className="min-w-0 mt-1">
                <p className="text-[15px]  font-bold text-gray-700 dark:text-white truncate">
                  {greeting}
                  {hasResolvedUser ? `, ${userName}` : ""}
                </p>
                <div
                  ref={mobileCityDropdownRef}
                  className="relative -ml-0.5"
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setCityDropdownOpen((prev) => !prev);
                    }}
                    className="flex items-center gap-0.5 px-0.5 py-0.5 text-xs text-gray-400 dark:text-gray-400"
                  >
                    <span className="truncate max-w-[110px]">
                      {locationLoading
                        ? "Detecting..."
                        : selectedCity || "Select City"}
                    </span>
                    <ChevronDown
                      size={11}
                      className={`transition-transform ${cityDropdownOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {cityDropdownOpen && (
                    <div className="absolute left-0 top-full mt-1 w-44 max-h-[253px] overflow-y-auto show-scrollbar bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-700 rounded-2xl shadow-2xl z-50 py-2 px-1">
                      <button
                        onClick={() => {
                          detectLocation();
                          setCityDropdownOpen(false);
                        }}
                        className="flex items-center gap-2 w-full text-left px-2 py-2 text-base rounded-full transition-all duration-200 text-gray-700 dark:text-gray-300 hover:bg-[#3B82F6] hover:text-white mb-1"
                      >
                        <LocationPinIcon />
                        <span className="whitespace-nowrap">
                          Detect My Location
                        </span>
                      </button>
                      <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
                      {cities.map((city) => (
                        <button
                          key={city}
                          className={`flex items-center gap-2 w-full text-left px-2 py-2 mt-1 text-base rounded-full transition-all duration-200 ${
                            selectedCity === city
                              ? "bg-[#2563EB] text-white"
                              : "text-gray-700 dark:text-gray-300 hover:bg-[#3B82F6] hover:text-white"
                          }`}
                          onClick={() => {
                            setManualCity(city);
                            setCityDropdownOpen(false);
                          }}
                        >
                          <LocationPinIcon />
                          {city}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Link>

            {/* Mobile Search + Theme Toggle */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                ref={mobileSearchToggleRef}
                type="button"
                onClick={() => setIsMobileSearchOpen((v) => !v)}
                className="p-2 rounded-full text-gray-500 dark:text-gray-300 hover:text-blue-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
                aria-label={isMobileSearchOpen ? "Close search" : "Open search"}
              >
                {isMobileSearchOpen ? <span className="block w-[18px] text-center text-xl leading-[18px]">×</span> : <SearchIcon />}
              </button>
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-full text-gray-500 dark:text-gray-300 hover:text-blue-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
                aria-label="Toggle dark mode"
              >
                {darkMode ? <SunIcon /> : <MoonIcon />}
              </button>
              
              {hasResolvedUser ? (
                <div className="relative" ref={mobileProfileDropdownRef}>
                  <button
                    type="button"
                    onClick={() => {
                      setIsMenuOpen(false);
                      setIsMobileProfileDropdownOpen((open) => !open);
                    }}
                    className="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0"
                    aria-label="Account"
                    aria-expanded={isMobileProfileDropdownOpen}
                  >
                    {user.avatar ? (
                      <img
                        src={user.avatar}
                        alt={userName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <svg
                        className="w-5 h-5 text-white"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <circle cx="12" cy="8" r="4" />
                        <path d="M4 20c0-4 4-7 8-7s8 3 8 7" />
                      </svg>
                    )}
                  </button>
                  {isMobileProfileDropdownOpen &&
                    (isSeller ? (
                      <SellerProfileDropdownMenu
                        user={user}
                        toggleDarkMode={toggleDarkMode}
                        darkMode={darkMode}
                        onClose={() => setIsMobileProfileDropdownOpen(false)}
                      />
                    ) : (
                      <ProfileDropdownMenu
                        user={user}
                        toggleDarkMode={toggleDarkMode}
                        darkMode={darkMode}
                        onClose={() => setIsMobileProfileDropdownOpen(false)}
                      />
                    ))}
                </div>
              ) : (
                <Link
                  to="/login"
                  className="bg-[#EFF6FF] dark:bg-gray-400 dark:text-white h-[28px] w-[30px] rounded-full flex items-center justify-center flex-shrink-0"
                  aria-label="Log in"
                >
                  <UserIcon />
                </Link>
              )}
            </div>
          </div>

          {isMobileSearchOpen && (
            <form
              className="relative mt-3"
              onSubmit={handleSearchSubmit}
              ref={mobileSearchContainerRef}
            >
              <div className="flex h-12 items-center gap-1 rounded-xl border border-[#D3D4D8] bg-[#F4F4F5] px-4 shadow-sm transition-all hover:border-blue-300 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/15 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-blue-500">
                <button type="submit" className="flex-shrink-0 text-gray-400 transition-colors hover:text-blue-600" aria-label="Search products">
                  <SearchIcon size={16} />
                </button>
                <input
                  ref={mobileSearchInputRef}
                  type="text"
                  placeholder="Search product, home, bike..."
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  onKeyDown={handleSearchKeyDown}
                  onFocus={openSearchDiscovery}
                  className="flex-1 min-w-0 bg-transparent text-base text-gray-700 dark:text-gray-200 placeholder-gray-400 outline-none"
                />
                {searchQuery && (
                  <button type="button" onClick={clearNavbarSearch} className="flex-shrink-0 rounded-full px-1 text-lg leading-none text-gray-400" aria-label="Clear product search">×</button>
                )}
              </div>
              {showSuggestions && <SuggestionDropdown />}
            </form>
          )}
        </div>

        {/* ── Mobile Menu (opens from bottom-nav Profile tab) ── */}
        <div
          className={`md:hidden fixed inset-0 bg-gray-50 dark:bg-gray-950 z-50 transition-all duration-300 overflow-y-auto pb-20 ${
            isMenuOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
          }`}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-gray-900 px-4 py-4 flex items-center justify-between border-b border-gray-100 dark:border-gray-800 shadow-sm z-10">
            <button
              onClick={() => setIsMenuOpen(false)}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
            </button>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">Menu</h1>
            <div className="w-8"></div>
          </div>

          <div className="px-4 py-4 space-y-6 max-w-md mx-auto">
            {/* ── LOGIN CTA (for signed-out users, shown first) ── */}
            {!hasResolvedUser && !isResolvingSessionUser && (
              <Link
                to="/login"
                className="flex items-center gap-3.5 p-4 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl transition-colors no-underline font-semibold w-full"
                onClick={() => setIsMenuOpen(false)}
              >
                <div className="p-2.5 bg-white/20 text-white rounded-xl">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 4-7 8-7s8 3 8 7"/></svg>
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-base font-semibold">Log in to your account</h2>
                  <p className="text-xs text-white/80 truncate">Access your orders, listings and wishlist</p>
                </div>
                <svg className="text-white" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
              </Link>
            )}

            {/* ── ACCOUNT ── */}
            {hasResolvedUser && (
              <div>
                <p className="text-xs font-bold tracking-wider text-gray-400 uppercase mb-2 px-1">Account</p>
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden divide-y divide-gray-100 dark:divide-gray-800 shadow-sm">
                  <Link
                    to="/account"
                    className="flex items-center gap-3.5 p-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors no-underline"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {user?.avatar ? (
                        <img src={user.avatar} alt={userName} className="w-full h-full object-cover" />
                      ) : (
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 4-7 8-7s8 3 8 7"/></svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-base font-semibold text-gray-900 dark:text-white">{userName}</h2>
                      <p className="text-xs text-gray-400 truncate">View profile and account settings</p>
                    </div>
                    <svg className="text-gray-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                  </Link>

                  <button
                    type="button"
                    onClick={() => {
                      setIsMenuOpen(false);
                      setShowLogoutModal(true);
                    }}
                    className="flex items-center gap-3.5 p-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left w-full"
                  >
                    <div className="p-2.5 bg-red-50 dark:bg-red-950/50 text-red-600 rounded-xl">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-base font-semibold text-red-600 dark:text-red-400">Logout</h2>
                      <p className="text-xs text-gray-400 truncate">Sign out from account</p>
                    </div>
                    <svg className="text-gray-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                </div>
              </div>
            )}

            {/* ── EXPLORE ── */}
            <div>
              <p className="text-xs font-bold tracking-wider text-gray-400 uppercase mb-2 px-1">Explore</p>
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden divide-y divide-gray-100 dark:divide-gray-800 shadow-sm">
                <Link
                  to="/products"
                  className="flex items-center gap-3.5 p-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors no-underline"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <div className="p-2.5 bg-blue-50 dark:bg-blue-950/50 text-blue-600 rounded-xl">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white">Products</h2>
                    <p className="text-xs text-gray-400 truncate">Browse all products</p>
                  </div>
                  <svg className="text-gray-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                </Link>

                <Link
                  to="/wishlist"
                  className="flex items-center gap-3.5 p-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors no-underline"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <div className="p-2.5 bg-blue-50 dark:bg-blue-950/50 text-blue-600 rounded-xl">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z"/></svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white">Wishlist</h2>
                    <p className="text-xs text-gray-400 truncate">Your saved items</p>
                  </div>
                  {wishlistCount > 0 && (
                    <span className="bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                      {wishlistCount}
                    </span>
                  )}
                  <svg className="text-gray-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                </Link>
              </div>
            </div>

            {/* ── SELLING ── */}
            <div>
              <p className="text-xs font-bold tracking-wider text-gray-400 uppercase mb-2 px-1">Selling</p>
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden divide-y divide-gray-100 dark:divide-gray-800 shadow-sm">
                {hasResolvedUser && isSeller ? (
                  <>
                    <Link
                      to="/seller"
                      className="flex items-center gap-3.5 p-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors no-underline"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <div className="p-2.5 bg-blue-50 dark:bg-blue-950/50 text-blue-600 rounded-xl">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"/>
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Dashboard</h2>
                        <p className="text-xs text-gray-400 truncate">Seller dashboard</p>
                      </div>
                      <svg className="text-gray-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                    </Link>

                    <Link
                      to="/seller/listings"
                      className="flex items-center gap-3.5 p-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors no-underline"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <div className="p-2.5 bg-blue-50 dark:bg-blue-950/50 text-blue-600 rounded-xl">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h2 className="text-base font-semibold text-gray-900 dark:text-white">My Listings</h2>
                        <p className="text-xs text-gray-400 truncate">Manage your products</p>
                      </div>
                      <svg className="text-gray-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                    </Link>

                    <Link
                      to="/account"
                      className="flex items-center gap-3.5 p-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors no-underline"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <div className="p-2.5 bg-blue-50 dark:bg-blue-950/50 text-blue-600 rounded-xl">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M12 1v6m0 6v6M4.22 4.22l4.24 4.24m0 5.08l-4.24 4.24M1 12h6m6 0h6M4.22 19.78l4.24-4.24m5.08 0l4.24 4.24M19.78 19.78l-4.24-4.24m0-5.08l4.24-4.24M23 12h-6m-6 0H5M19.78 4.22l-4.24 4.24m-5.08 0l-4.24-4.24"/></svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Account</h2>
                        <p className="text-xs text-gray-400 truncate">Profile, security and preferences</p>
                      </div>
                      <svg className="text-gray-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                    </Link>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      if (!hasResolvedUser) {
                        navigate("/login?from=" + encodeURIComponent("/seller/listings/add"));
                      } else {
                        navigate("/seller/listings/add");
                      }
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center gap-3.5 p-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left w-full"
                  >
                    <div className="p-2.5 bg-blue-50 dark:bg-blue-950/50 text-blue-600 rounded-xl">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-base font-semibold text-gray-900 dark:text-white">Become a Seller</h2>
                      <p className="text-xs text-gray-400 truncate">Start listing your products</p>
                    </div>
                    <svg className="text-gray-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                )}
              </div>
            </div>

            {/* ── PREFERENCES ── */}
            <div>
              <p className="text-xs font-bold tracking-wider text-gray-400 uppercase mb-2 px-1">Preferences</p>
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden divide-y divide-gray-100 dark:divide-gray-800 shadow-sm">
                <button
                  type="button"
                  onClick={() => {
                    toggleDarkMode();
                    setIsMenuOpen(false);
                  }}
                  className="flex items-center gap-3.5 p-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left w-full"
                >
                  <div className="p-2.5 bg-blue-50 dark:bg-blue-950/50 text-blue-600 rounded-xl">
                    {darkMode ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white">Theme</h2>
                    <p className="text-xs text-gray-400 truncate">{darkMode ? "Dark mode" : "Light mode"}</p>
                  </div>
                  <svg className="text-gray-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                </button>

                <Link
                  to="/faq"
                  className="flex items-center gap-3.5 p-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors no-underline"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <div className="p-2.5 bg-blue-50 dark:bg-blue-950/50 text-blue-600 rounded-xl">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white">FAQ</h2>
                    <p className="text-xs text-gray-400 truncate">Help and questions</p>
                  </div>
                  <svg className="text-gray-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                </Link>

                <Link
                  to="/contact"
                  className="flex items-center gap-3.5 p-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors no-underline"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <div className="p-2.5 bg-blue-50 dark:bg-blue-950/50 text-blue-600 rounded-xl">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white">Contact Us</h2>
                    <p className="text-xs text-gray-400 truncate">Get in touch</p>
                  </div>
                  <svg className="text-gray-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* ── Logout Modal ── */}
      {showLogoutModal && (
        <div
          className="fixed inset-0 flex items-center justify-center z-[9999]"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            animation: "fadeIn 0.3s ease-in-out forwards",
          }}
          onClick={handleBackdropClick}
        >
          {/* ── DESKTOP POPUP ── */}
          <div
            className="hidden md:block bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-8 w-96 relative"
            style={{
              animation:
                "slideUp 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.3)",
            }}
          >
            {/* Loading overlay during logout */}
            {isLoggingOut && (
              <div className="absolute inset-0 bg-white/40 dark:bg-gray-800/40 rounded-xl flex items-center justify-center backdrop-blur-sm z-10">
                <div className="text-center">
                  <svg
                    className="animate-spin h-10 w-10 text-blue-500 mx-auto"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  <p className="text-base text-gray-700 dark:text-gray-300 mt-3 font-semibold">
                    Logging out...
                  </p>
                </div>
              </div>
            )}

            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2 text-center">
              Confirm Logout
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8 text-center text-base">
              Are you sure you want to log out? You'll need to log in again to
              access your account.
            </p>

            <div className="flex gap-3">
              <button
                disabled={isLoggingOut}
                className="flex-1 px-4 py-3 text-base font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => !isLoggingOut && setShowLogoutModal(false)}
              >
                Cancel
              </button>
              <button
                disabled={isLoggingOut}
                className="flex-1 px-4 py-3 text-base font-semibold text-white bg-red-500 rounded-lg hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                onClick={handleLogoutClick}
              >
                {isLoggingOut ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      />
                    </svg>
                    <span>Logging out...</span>
                  </>
                ) : (
                  "Logout"
                )}
              </button>
            </div>
          </div>

          {/* ── MOBILE POPUP ── */}
          <div
            className="md:hidden m-2 bg-white dark:bg-gray-800 !rounded-xl shadow-2xl p-6 w-full fixed bottom-0 left-0 relative"
            style={{
              animation:
                "slideUpMobile 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards",
              maxHeight: "80vh",
              overflowY: "auto",
              borderRadius: "24px 24px 0 0",
            }}
          >
            {/* Loading overlay during logout */}
            {isLoggingOut && (
              <div className="absolute inset-0 bg-white/40 dark:bg-gray-800/40 rounded-3xl flex items-center justify-center backdrop-blur-sm z-10">
                <div className="text-center">
                  <svg
                    className="animate-spin h-12 w-12 text-blue-500 mx-auto"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  <p className="text-base text-gray-700 dark:text-gray-300 mt-4 font-bold">
                    Logging out...
                  </p>
                </div>
              </div>
            )}

            {/* Handle bar */}

            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2 text-center">
              Logout?
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8 text-center text-base leading-relaxed">
              You'll need to log in again to access your account and all your
              listings.
            </p>

            <div className="space-y-3">
              <button
                disabled={isLoggingOut}
                className="w-full px-4 py-3.5 text-base font-bold text-white bg-red-500 rounded-xl hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 active:scale-95"
                onClick={handleLogoutClick}
              >
                {isLoggingOut ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      />
                    </svg>
                    <span>Logging out...</span>
                  </>
                ) : (
                  "Yes, Logout"
                )}
              </button>
              <button
                disabled={isLoggingOut}
                className="w-full px-4 py-3.5 text-base font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
                onClick={() => !isLoggingOut && setShowLogoutModal(false)}
              >
                Keep Me Logged In
              </button>
            </div>
          </div>

          <style>{`
            @keyframes fadeIn {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }

            @keyframes slideUp {
              from {
                opacity: 0;
                transform: translateY(20px) scale(0.95);
              }
              to {
                opacity: 1;
                transform: translateY(0) scale(1);
              }
            }

            @keyframes slideUpMobile {
              from {
                opacity: 0;
                transform: translateY(100px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
          `}</style>
        </div>
      )}

      {/* ── Mobile Bottom Navigation ── */}
      <div data-mobile-bottom-nav={showMobileBottomNav ? "visible" : "hidden"} className={`md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 z-40 flex items-center justify-around h-20 transition-transform duration-300 ${showMobileBottomNav ? "translate-y-0" : "translate-y-full"}`}>
        {/* Home */}
        <Link
          to="/"
          className={`flex flex-col items-center justify-center w-1/5 h-full gap-1 no-underline transition-colors ${
            location.pathname === "/"
              ? "text-blue-600 dark:text-blue-500"
              : "text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
          }`}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
          <span className="text-xs font-medium">Home</span>
        </Link>

        {/* Explore */}
        <Link
          to="/products"
          className={`flex flex-col items-center justify-center w-1/5 h-full gap-1 no-underline transition-colors ${
            location.pathname === "/products"
              ? "text-blue-600 dark:text-blue-500"
              : "text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
          }`}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <span className="text-xs font-medium">Explore</span>
        </Link>

        {/* List */}
        <button
          type="button"
          onClick={() => {
            if (!hasResolvedUser) {
              navigate("/login?from=" + encodeURIComponent("/seller/listings/add"));
            } else {
              navigate("/seller/listings/add");
            }
          }}
          className="flex flex-col items-center justify-center w-1/5 h-full gap-1 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
        >
          <div className="bg-blue-600 p-2.5 rounded-full">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
          </div>
          <span className="text-xs font-medium">List</span>
        </button>

        {/* Wishlist */}
        <Link
          to="/wishlist"
          className={`flex flex-col items-center justify-center w-1/5 h-full gap-1 no-underline transition-colors relative ${
            location.pathname === "/wishlist"
              ? "text-blue-600 dark:text-blue-500"
              : "text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
          }`}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
          {wishlistCount > 0 && (
            <span className="absolute top-2 right-4 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold">
              {wishlistCount}
            </span>
          )}
          <span className="text-xs font-medium">Wishlist</span>
        </Link>

        {/* Profile — opens the mobile menu */}
        <button
          type="button"
          onClick={() => {
            setIsMobileProfileDropdownOpen(false);
            setIsMenuOpen((open) => !open);
          }}
          className={`flex flex-col items-center justify-center w-1/5 h-full gap-1 transition-colors ${
            isMenuOpen
              ? "text-blue-600 dark:text-blue-500"
              : "text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
          }`}
        >
          {hasResolvedUser ? (
            <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center overflow-hidden">
              {user.avatar ? (
                <img
                  src={user.avatar}
                  alt={userName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <svg
                  className="w-3.5 h-3.5 text-white"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <circle cx="12" cy="8" r="4" />
                  <path d="M4 20c0-4 4-7 8-7s8 3 8 7" />
                </svg>
              )}
            </div>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 4-7 8-7s8 3 8 7"/>
            </svg>
          )}
          <span className="text-xs font-medium">Account</span>
        </button>
      </div>
    </>
  );
};

export default Navbar;
